import React, { useEffect, useState } from "react";
import AppLayout from "components/ui/AppLayout";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { adminApi } from "api/admin";
import { getCurrentUser } from "api/client";

const STEP_LABELS = [
  ["signup", "Signed up"],
  ["whatsappConnected", "WhatsApp"],
  ["hasData", "Data in"],
  ["firstFollowUpSent", "1st AI follow-up"],
];

function StatCard({ icon, label, value, sub, tone }) {
  return (
    <div
      className="rounded-xl border p-4 flex items-start gap-3"
      style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: tone || "var(--color-muted)", color: "var(--color-foreground)" }}
      >
        <Icon name={icon} size={18} />
      </div>
      <div className="min-w-0">
        <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>{label}</p>
        <p className="font-heading font-bold text-2xl leading-tight" style={{ color: "var(--color-foreground)" }}>{value}</p>
        {sub && <p className="font-caption text-xs mt-0.5" style={{ color: "var(--color-text-secondary)" }}>{sub}</p>}
      </div>
    </div>
  );
}

export default function PilotOverview() {
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const currentUser = getCurrentUser();
  const isOwner = currentUser?.role === "Owner";

  const load = async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const [ops, funnelData] = await Promise.all([adminApi.opsSummary(), adminApi.funnel()]);
      setSummary(ops);
      setFunnel(funnelData);
    } catch (error) {
      setLoadError(error?.message || "Unable to load pilot overview.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOwner) load();
  }, []);

  return (
    <AppLayout>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: "var(--color-foreground)", letterSpacing: "-0.02em" }}>
              Pilot Overview
            </h1>
            <p className="font-caption text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
              Day-0 ops checklist + north-star funnel across pilot orgs (Owner only)
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={isLoading}>
            <Icon name="RefreshCw" size={14} style={{ marginRight: 6 }} />
            {isLoading ? "Loading…" : "Refresh"}
          </Button>
        </div>

        {!isOwner ? (
          <div
            className="rounded-xl border p-8 text-center"
            style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}
          >
            <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>
              This view is for the workspace Owner only. Ask your owner to open it.
            </p>
          </div>
        ) : (
          <>
            {loadError && (
              <div className="mb-4 rounded-lg border px-4 py-3" style={{ borderColor: "var(--color-error)", background: "var(--color-card)" }}>
                <p className="font-caption text-sm" style={{ color: "var(--color-error)" }}>{loadError}</p>
              </div>
            )}

            {isLoading && !summary ? (
              <p className="font-caption text-sm py-10 text-center" style={{ color: "var(--color-text-secondary)" }}>
                Loading pilot data…
              </p>
            ) : summary ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-6">
                  <StatCard icon="Building2" label="Orgs" value={summary.orgs.total} sub={`${summary.orgs.withWhatsApp} with WhatsApp · ${summary.orgs.active} active`} />
                  <StatCard icon="Users" label="Users" value={summary.users.total} />
                  <StatCard icon="MessageSquare" label="Open conversations" value={summary.conversations.open} sub={`${summary.conversations.total} total threads`} />
                  <StatCard icon="Send" label="Outbound (24h)" value={summary.messages.outboundLast24h} />
                  <StatCard icon="Database" label="Leads / Contacts / Tasks" value={`${summary.records.leads} / ${summary.records.contacts} / ${summary.records.tasks}`} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
                  <StatCard icon="Sparkles" label="AI tokens today / month" value={`${summary.aiUsage.todayTokens.toLocaleString()} / ${summary.aiUsage.monthTokens.toLocaleString()}`} sub="monthly budget guard applies per org" />
                  <StatCard icon="Clock" label="Last message" value={summary.lastMessageAtUtc ? new Date(summary.lastMessageAtUtc).toLocaleString("en-IN") : "—"} sub="across all orgs" />
                </div>

                <div className="rounded-xl border p-4 mb-6" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
                  <h2 className="font-heading font-semibold text-lg mb-3" style={{ color: "var(--color-foreground)" }}>
                    Queue health
                  </h2>
                  {summary.queues.length === 0 ? (
                    <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>No queues registered.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="font-caption" style={{ color: "var(--color-text-secondary)" }}>
                            <th className="text-left py-1.5 pr-3 font-medium">Queue</th>
                            <th className="text-right py-1.5 px-2 font-medium">Waiting</th>
                            <th className="text-right py-1.5 px-2 font-medium">Active</th>
                            <th className="text-right py-1.5 px-2 font-medium">Failed</th>
                            <th className="text-right py-1.5 pl-2 font-medium">DLQ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.queues.map((q) => (
                            <tr key={q.name} className="border-t" style={{ borderColor: "var(--color-border)" }}>
                              <td className="py-2 pr-3 font-caption font-medium" style={{ color: "var(--color-foreground)" }}>{q.name}</td>
                              <td className="py-2 px-2 text-right font-caption">{q.waiting}</td>
                              <td className="py-2 px-2 text-right font-caption">{q.active}</td>
                              <td className="py-2 px-2 text-right font-caption" style={{ color: q.failed > 0 ? "var(--color-error)" : "inherit" }}>{q.failed}</td>
                              <td className="py-2 pl-2 text-right font-caption" style={{ color: q.dlq > 0 ? "var(--color-error)" : "inherit" }}>{q.dlq}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="rounded-xl border p-4" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                    <h2 className="font-heading font-semibold text-lg" style={{ color: "var(--color-foreground)" }}>
                      North-star funnel
                    </h2>
                    <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>
                      {funnel?.completed ?? 0} of {funnel?.total ?? 0} orgs completed the 10-minute onboarding
                    </p>
                  </div>
                  {funnel?.items?.length === 0 ? (
                    <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>No pilot orgs yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="font-caption" style={{ color: "var(--color-text-secondary)" }}>
                            <th className="text-left py-1.5 pr-3 font-medium">Org</th>
                            <th className="text-left py-1.5 px-2 font-medium">Vertical</th>
                            {STEP_LABELS.map(([, label]) => (
                              <th key={label} className="text-center py-1.5 px-1 font-medium">{label}</th>
                            ))}
                            <th className="text-right py-1.5 px-2 font-medium">Time to north-star</th>
                          </tr>
                        </thead>
                        <tbody>
                          {funnel.items.map((org) => (
                            <tr key={org.id} className="border-t" style={{ borderColor: "var(--color-border)" }}>
                              <td className="py-2 pr-3">
                                <span className="font-caption font-medium" style={{ color: "var(--color-foreground)" }}>{org.name}</span>
                                {org.complete && (
                                  <span
                                    className="ml-2 inline-block px-1.5 py-0.5 rounded-full font-caption text-[11px] font-semibold"
                                    style={{ background: "#DCFCE7", color: "#15803D" }}
                                  >
                                    ✓ complete
                                  </span>
                                )}
                              </td>
                              <td className="py-2 px-2 font-caption">{org.vertical ?? "—"}</td>
                              {STEP_LABELS.map(([key]) => (
                                <td key={key} className="py-2 px-1 text-center font-caption">
                                  {org.steps[key] ? (
                                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full" style={{ background: "#DCFCE7", color: "#15803D" }}>
                                      <Icon name="Check" size={12} />
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full" style={{ background: "var(--color-muted)", color: "var(--color-text-secondary)" }}>
                                      <Icon name="Minus" size={12} />
                                    </span>
                                  )}
                                </td>
                              ))}
                              <td className="py-2 px-2 text-right font-caption" style={{ color: "var(--color-text-secondary)" }}>
                                {org.timeToNorthStarMinutes === null
                                  ? "—"
                                  : org.timeToNorthStarMinutes < 60
                                    ? `${org.timeToNorthStarMinutes}m`
                                    : `${Math.floor(org.timeToNorthStarMinutes / 60)}h ${org.timeToNorthStarMinutes % 60}m`}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </>
        )}
      </div>
    </AppLayout>
  );
}
